/**
 * Device Service
 * Handles device-based authentication and auto-login functionality.
 * Uses Capacitor Device plugin to get unique device identifiers.
 */

import { Device } from '@capacitor/device';
import { Capacitor } from '@capacitor/core';
import { CapacitorHttp } from '@capacitor/core';
import axios from 'axios';
import { getCurrentApiBaseUrl } from '../config';

/**
 * Gets a unique device identifier.
 * On native platforms, uses Capacitor Device.getId() which provides a UUID.
 * On web, falls back to localStorage-based ID.
 */
export const getDeviceId = async () => {
  try {
    if (Capacitor.isNativePlatform()) {
      const info = await Device.getId();
      return info.identifier; // UUID that persists across app reinstalls (unless factory reset)
    } else {
      // Web fallback - use localStorage-based ID
      let webDeviceId = localStorage.getItem('web_device_id');
      if (!webDeviceId) {
        webDeviceId = 'web_' + crypto.randomUUID();
        localStorage.setItem('web_device_id', webDeviceId);
      }
      return webDeviceId;
    }
  } catch (error) {
    console.error('Failed to get device ID:', error);
    return null;
  }
};

/**
 * Gets device info for display purposes.
 */
export const getDeviceInfo = async () => {
  try {
    if (Capacitor.isNativePlatform()) {
      const info = await Device.getInfo();
      return {
        name: `${info.manufacturer} ${info.model}`,
        platform: info.platform,
        osVersion: info.osVersion,
        model: info.model,
        manufacturer: info.manufacturer,
      };
    } else {
      return {
        name: 'Web Browser',
        platform: 'web',
        osVersion: navigator.userAgent,
        model: 'Browser',
        manufacturer: 'N/A',
      };
    }
  } catch (error) {
    console.error('Failed to get device info:', error);
    return {
      name: 'Unbekanntes Gerät',
      platform: 'unknown',
    };
  }
};

/**
 * Makes HTTP request using native HTTP on mobile or axios on web.
 */
const makeRequest = async (method, url, data = null) => {
  const baseUrl = getCurrentApiBaseUrl();
  const fullUrl = `${baseUrl}${url}`;

  if (Capacitor.isNativePlatform()) {
    try {
      const response = await CapacitorHttp.request({
        url: fullUrl,
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        data: data ? JSON.stringify(data) : undefined,
      });
      return {
        data: response.data,
        status: response.status,
      };
    } catch (error) {
      throw {
        response: {
          status: error.status || 0,
          data: error.data || { detail: error.message },
        },
      };
    }
  } else {
    return axios({
      method,
      url: fullUrl,
      data,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }
};

/**
 * Attempts auto-login using device ID.
 * Returns token info if device is registered, null otherwise.
 */
export const tryDeviceAuth = async () => {
  const deviceId = await getDeviceId();
  if (!deviceId) {
    console.log('No device ID available');
    return null;
  }

  try {
    const response = await makeRequest('POST', '/api/auth/device/', {
      device_id: deviceId,
    });

    if (response.status === 200) {
      console.log('Device auto-login successful:', response.data.username);
      return {
        token: response.data.token,
        userId: response.data.user_id,
        username: response.data.username,
        deviceName: response.data.device_name,
      };
    }
    return null;
  } catch (error) {
    if (error.response?.status === 404) {
      // Device not registered - this is expected for new devices
      console.log('Device not registered yet');
    } else if (error.response?.status === 401) {
      // Token expired
      console.log('Device token expired');
    } else {
      console.error('Device auth error:', error);
    }
    return null;
  }
};

/**
 * Registers device ID with an existing API token.
 * Call this after successful QR code login or manual login.
 */
export const registerDevice = async (token) => {
  const deviceId = await getDeviceId();
  const deviceInfo = await getDeviceInfo();

  if (!deviceId) {
    console.error('Cannot register device: no device ID');
    return false;
  }

  try {
    const response = await makeRequest('POST', '/api/auth/device/register/', {
      token: token,
      device_id: deviceId,
      device_name: deviceInfo.name,
    });

    if (response.status === 200) {
      console.log('Device registered successfully:', deviceInfo.name);
      return true;
    }
    return false;
  } catch (error) {
    console.error('Device registration failed:', error);
    return false;
  }
};

export default {
  getDeviceId,
  getDeviceInfo,
  tryDeviceAuth,
  registerDevice,
};
